import { useState, useEffect } from 'react';
import { X, ArrowRight, ArrowLeft, Building2, MapPin, Phone, Zap, FileText, PenTool } from 'lucide-react';
import { cn } from '@/lib/utils';
import { WizardStepper, type WizardStep } from '@/components/ui/wizard-stepper';
import { ToggleGroup } from '@/components/ui/toggle-group';
import { useSubmitPortOrderWithSignature } from '../hooks';
import { US_STATES } from '@/lib/constants';
import { SignaturePad, SignatureData } from './signature-pad';
import type { PortOrderWithSignatureRequest } from '../types';

interface PortOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  service_type: 'residential' | 'business';
  company_name: string;
  email_address: string;
  account_owner_name: string;
  authorized_title: string;
  contact_phone: string;
  account_number: string;
  account_pin: string;
  billing_telephone_number: string;
  service_address: string;
  service_address_line2: string;
  service_city: string;
  service_state: string;
  service_postal_code: string;
  losing_carrier: string;
  requested_foc_date: string | null;
  remaining_numbers_action: 'disconnect' | 'keep';
  contains_wireless: 'no' | 'yes';
  telephone_numbers: string;
}

const initialFormData: FormData = {
  service_type: 'business',
  company_name: '',
  email_address: '',
  account_owner_name: '',
  authorized_title: '',
  contact_phone: '',
  account_number: '',
  account_pin: '',
  billing_telephone_number: '',
  service_address: '',
  service_address_line2: '',
  service_city: '',
  service_state: 'TX',
  service_postal_code: '',
  losing_carrier: '',
  requested_foc_date: null,
  remaining_numbers_action: 'keep',
  contains_wireless: 'no',
  telephone_numbers: '',
};

const wizardSteps: WizardStep[] = [
  { id: 1, label: 'Account Info' },
  { id: 2, label: 'Number List' },
  { id: 3, label: 'Port Details' },
  { id: 4, label: 'Review & Sign' },
];

export function PortOrderModal({ isOpen, onClose }: PortOrderModalProps) {
  const submitMutation = useSubmitPortOrderWithSignature();

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [signatureData, setSignatureData] = useState<SignatureData | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [step, setStep] = useState(1);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData(initialFormData);
      setSignatureData(null);
      setErrors({});
      setStep(1);
    }
  }, [isOpen]);

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const formatPhoneInput = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length >= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length >= 3) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }
    return digits;
  };

  const validateStep1 = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.company_name.trim()) newErrors.company_name = 'Customer name is required';
    if (!formData.email_address.trim()) newErrors.email_address = 'Email is required';
    if (!formData.account_owner_name.trim())
      newErrors.account_owner_name = 'Authorizing person name is required';
    if (!formData.authorized_title.trim())
      newErrors.authorized_title = 'Title is required';
    if (!formData.losing_carrier.trim())
      newErrors.losing_carrier = 'Current carrier is required';
    if (!formData.contact_phone.trim())
      newErrors.contact_phone = 'Contact phone is required';
    if (formData.contact_phone && formData.contact_phone.replace(/\D/g, '').length !== 10)
      newErrors.contact_phone = 'Contact phone must be 10 digits';
    if (!formData.service_address.trim()) newErrors.service_address = 'Address is required';
    if (!formData.service_city.trim()) newErrors.service_city = 'City is required';
    if (!formData.service_state) newErrors.service_state = 'State is required';
    if (!formData.service_postal_code.trim()) newErrors.service_postal_code = 'ZIP code is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.telephone_numbers.trim())
      newErrors.telephone_numbers = 'At least one phone number is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep4 = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!signatureData) {
      newErrors.signature = 'Please sign the Letter of Authorization';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    if (step < 4) setStep(step + 1);
  };

  const handlePreviousStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep4()) return;
    if (!signatureData) return;

    const fullServiceAddress = formData.service_address_line2
      ? `${formData.service_address}, ${formData.service_address_line2}`
      : formData.service_address;

    const requestData: PortOrderWithSignatureRequest = {
      service_type: formData.service_type,
      company_name: formData.company_name,
      email_address: formData.email_address,
      account_owner_name: formData.account_owner_name,
      authorized_title: formData.authorized_title,
      contact_phone: formData.contact_phone?.replace(/\D/g, '') || '',
      account_number: formData.account_number || '',
      account_pin: formData.account_pin || '',
      billing_telephone_number: formData.billing_telephone_number?.replace(/\D/g, '') || '',
      service_address: fullServiceAddress,
      service_city: formData.service_city,
      service_state: formData.service_state,
      service_postal_code: formData.service_postal_code,
      losing_carrier: formData.losing_carrier,
      requested_foc_date: formData.requested_foc_date || undefined,
      remaining_numbers_action: formData.remaining_numbers_action,
      contains_wireless: formData.contains_wireless,
      telephone_numbers: formData.telephone_numbers,
      signature_type: signatureData.type,
      signature_data: signatureData.data,
    };

    try {
      await submitMutation.mutateAsync(requestData);
      onClose();
    } catch {
      // Error handled by mutation
    }
  };

  const handleClose = () => {
    if (!submitMutation.isPending) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const inputClasses =
    'w-full px-4 py-3 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent placeholder:text-slate-400';
  const labelClasses = 'block text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2';
  const errorClasses = 'mt-1.5 text-xs text-red-500';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col overflow-hidden animate-fade-in">
        {/* Dark Header with Stepper */}
        <div className="bg-slate-900 text-white px-6 py-5">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-500 rounded-xl flex items-center justify-center">
                <ArrowRight size={20} className="rotate-180" />
                <ArrowLeft size={20} className="ml-[-12px]" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">Port My Numbers</h2>
                <p className="text-xs text-slate-400">Migration Wizard v3.4</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Stepper */}
          <WizardStepper steps={wizardSteps} currentStep={step} />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Account Info */}
          {step === 1 && (
            <div className="space-y-6 animate-fade-in">
              {/* Carrier Account Details */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Building2 size={18} className="text-slate-400" />
                  <h3 className="font-semibold text-slate-900">Carrier Account Details</h3>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelClasses}>Customer Name</label>
                    <input
                      type="text"
                      value={formData.company_name}
                      onChange={(e) => handleChange('company_name', e.target.value)}
                      className={inputClasses}
                      placeholder="Acme Corp LLC"
                    />
                    {errors.company_name && <p className={errorClasses}>{errors.company_name}</p>}
                  </div>
                  <div>
                    <label className={labelClasses}>Email Address</label>
                    <input
                      type="email"
                      value={formData.email_address}
                      onChange={(e) => handleChange('email_address', e.target.value)}
                      className={inputClasses}
                      placeholder="admin@acme.com"
                    />
                    {errors.email_address && <p className={errorClasses}>{errors.email_address}</p>}
                  </div>
                  <div>
                    <label className={labelClasses}>Authorizing Person</label>
                    <input
                      type="text"
                      value={formData.account_owner_name}
                      onChange={(e) => handleChange('account_owner_name', e.target.value)}
                      className={inputClasses}
                      placeholder="John Doe"
                    />
                    {errors.account_owner_name && <p className={errorClasses}>{errors.account_owner_name}</p>}
                  </div>
                  <div>
                    <label className={labelClasses}>Contact Phone</label>
                    <input
                      type="text"
                      value={formData.contact_phone}
                      onChange={(e) => handleChange('contact_phone', formatPhoneInput(e.target.value))}
                      className={inputClasses}
                      placeholder="(555) 000-0000"
                    />
                    {errors.contact_phone && <p className={errorClasses}>{errors.contact_phone}</p>}
                  </div>
                  <div>
                    <label className={labelClasses}>Title</label>
                    <input
                      type="text"
                      value={formData.authorized_title}
                      onChange={(e) => handleChange('authorized_title', e.target.value)}
                      className={inputClasses}
                      placeholder="Manager"
                    />
                    {errors.authorized_title && <p className={errorClasses}>{errors.authorized_title}</p>}
                  </div>
                  <div>
                    <label className={labelClasses}>Current Carrier</label>
                    <input
                      type="text"
                      value={formData.losing_carrier}
                      onChange={(e) => handleChange('losing_carrier', e.target.value)}
                      className={inputClasses}
                      placeholder="e.g. AT&T, Verizon"
                    />
                    {errors.losing_carrier && <p className={errorClasses}>{errors.losing_carrier}</p>}
                  </div>
                  <div>
                    <label className={labelClasses}>Account Number</label>
                    <input
                      type="text"
                      value={formData.account_number}
                      onChange={(e) => handleChange('account_number', e.target.value)}
                      className={inputClasses}
                      placeholder="Required"
                    />
                  </div>
                  <div>
                    <label className={labelClasses}>Account PIN</label>
                    <input
                      type="text"
                      value={formData.account_pin}
                      onChange={(e) => handleChange('account_pin', e.target.value)}
                      className={inputClasses}
                      placeholder="If enabled on account"
                    />
                  </div>
                  <div>
                    <label className={labelClasses}>Billing Telephone Number (BTN)</label>
                    <input
                      type="text"
                      value={formData.billing_telephone_number}
                      onChange={(e) => handleChange('billing_telephone_number', formatPhoneInput(e.target.value))}
                      className={inputClasses}
                      placeholder="(555) 000-0000"
                    />
                  </div>
                </div>
              </div>

              {/* Service Address */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin size={18} className="text-slate-400" />
                  <h3 className="font-semibold text-slate-900">Service Address</h3>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className={labelClasses}>Street Address</label>
                    <input
                      type="text"
                      value={formData.service_address}
                      onChange={(e) => handleChange('service_address', e.target.value)}
                      className={inputClasses}
                      placeholder="123 Carrier Way"
                    />
                    {errors.service_address && <p className={errorClasses}>{errors.service_address}</p>}
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className={labelClasses}>City</label>
                      <input
                        type="text"
                        value={formData.service_city}
                        onChange={(e) => handleChange('service_city', e.target.value)}
                        className={inputClasses}
                        placeholder="San Francisco"
                      />
                      {errors.service_city && <p className={errorClasses}>{errors.service_city}</p>}
                    </div>
                    <div>
                      <label className={labelClasses}>State</label>
                      <select
                        value={formData.service_state}
                        onChange={(e) => handleChange('service_state', e.target.value)}
                        className={inputClasses}
                      >
                        {US_STATES.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.value}
                          </option>
                        ))}
                      </select>
                      {errors.service_state && <p className={errorClasses}>{errors.service_state}</p>}
                    </div>
                    <div>
                      <label className={labelClasses}>Zip Code</label>
                      <input
                        type="text"
                        value={formData.service_postal_code}
                        onChange={(e) => handleChange('service_postal_code', e.target.value)}
                        className={inputClasses}
                        placeholder="94105"
                        maxLength={10}
                      />
                      {errors.service_postal_code && <p className={errorClasses}>{errors.service_postal_code}</p>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Number List */}
          {step === 2 && (
            <div className="space-y-6 animate-fade-in">
              <div className="flex items-center gap-2 mb-4">
                <Phone size={18} className="text-slate-400" />
                <h3 className="font-semibold text-slate-900">Telephone Numbers to Port</h3>
              </div>

              <p className="text-sm text-slate-500">
                Enter one number per line in E.164 or local format.
              </p>

              <textarea
                value={formData.telephone_numbers}
                onChange={(e) => handleChange('telephone_numbers', e.target.value)}
                rows={10}
                className={cn(inputClasses, 'resize-none font-mono')}
                placeholder="2145551212&#10;2145551213"
              />
              {errors.telephone_numbers && <p className={errorClasses}>{errors.telephone_numbers}</p>}

              {/* Auto-validate callout */}
              <div className="flex items-center gap-3 p-4 bg-indigo-50 border border-indigo-100 rounded-xl">
                <Zap size={18} className="text-indigo-500" />
                <div className="flex-1">
                  <span className="text-sm font-medium text-slate-700">Auto-Validate on Submit</span>
                </div>
                <span className="text-sm text-indigo-500 font-medium">Powered by telviaIQ Engine</span>
              </div>
            </div>
          )}

          {/* Step 3: Port Details */}
          {step === 3 && (
            <div className="space-y-6 animate-fade-in">
              <h3 className="font-semibold text-slate-900">Execution Preferences</h3>

              {/* FOC Date */}
              <div>
                <label className={labelClasses}>Requested FOC Date</label>
                <input
                  type="date"
                  value={formData.requested_foc_date || ''}
                  onChange={(e) => handleChange('requested_foc_date', e.target.value || '')}
                  className={inputClasses}
                />
              </div>

              {/* Remaining Numbers */}
              <div>
                <label className={labelClasses}>Remaining Numbers on Account</label>
                <ToggleGroup
                  options={[
                    { value: 'disconnect', label: 'Disconnect' },
                    { value: 'keep', label: 'Keep in Service' },
                  ]}
                  value={formData.remaining_numbers_action}
                  onChange={(v) => handleChange('remaining_numbers_action', v)}
                />
              </div>

              {/* Wireless */}
              <div>
                <label className={labelClasses}>Contains Wireless Numbers?</label>
                <ToggleGroup
                  options={[
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' },
                  ]}
                  value={formData.contains_wireless}
                  onChange={(v) => handleChange('contains_wireless', v)}
                />
              </div>
            </div>
          )}

          {/* Step 4: Review & Sign */}
          {step === 4 && (
            <div className="space-y-6 animate-fade-in">
              {/* LOA Card */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <FileText size={18} className="text-slate-400" />
                  <h3 className="font-semibold text-slate-900">Letter of Agency (LOA)</h3>
                </div>

                <div className="text-sm text-slate-600 space-y-3">
                  <p>
                    I, the undersigned, authorize telviaIQ (the "Company") to act on behalf of the customer
                    listed below for the purpose of porting the telephone numbers specified. I understand
                    that this authorization grants Company the power to interact with my current service
                    provider, access my customer service records (CSR), and request the migration of services.
                  </p>
                  <p>
                    I verify that I am an authorized officer or employee of the account holder and have the
                    legal authority to sign this document. I acknowledge that porting may result in early
                    termination fees from my current carrier and that telviaIQ is not responsible for such fees.
                  </p>
                </div>
              </div>

              {/* Digital Signature */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <PenTool size={18} className="text-slate-400" />
                  <h3 className="font-semibold text-slate-900">Digital Signature</h3>
                </div>

                <SignaturePad onSignatureChange={setSignatureData} />
                {errors.signature && <p className={errorClasses}>{errors.signature}</p>}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-white">
          {step > 1 ? (
            <button
              onClick={handlePreviousStep}
              disabled={submitMutation.isPending}
              className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ArrowLeft size={16} />
              Previous Step
            </button>
          ) : (
            <div />
          )}

          {step < 4 ? (
            <button
              onClick={handleNextStep}
              className="flex items-center gap-2 px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-xl transition-colors"
            >
              Next Step
              <ArrowRight size={16} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitMutation.isPending}
              className={cn(
                'flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-medium transition-all',
                submitMutation.isPending
                  ? 'bg-slate-100 text-slate-400'
                  : 'bg-gradient-to-r from-cyan-500 to-indigo-500 hover:from-cyan-400 hover:to-indigo-400 text-white shadow-lg'
              )}
            >
              {submitMutation.isPending ? (
                <>
                  <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Port Request'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
